import { fetchMe, logout, openSignIn, avatarSrc, truncateName, classifyAuthState, BN_AUTH_CHANGE } from "@bn-games/auth-client";
import type { Profile } from "@bn-games/auth-client";
import { el, clear } from "./dom";
import { getLandingUrl } from "./landingUrl";
import { t } from "@bn-games/i18n-core";

export interface AuthWidgetHandle {
  readonly root: HTMLElement;
  destroy(): void;
}

export function createAuthWidget(): AuthWidgetHandle {
  const root = el("div", { class: "auth-widget" });

  let signinExpanded = false;
  let dropdownOpen = false;

  const landingUrl = getLandingUrl();

  function renderLoading(): void {
    clear(root);
  }

  // logout() clears the profile cache and dispatches bn_auth_change itself,
  // which the widget's own listener (below) already turns into a re-render —
  // callers must not also call render() or the widget re-renders twice.
  function createSignoutButton(className: string): HTMLButtonElement {
    return el("button", {
      class: className,
      text: t("auth.signOut"),
      on: {
        click: () => {
          void logout();
        },
      },
    });
  }

  function renderUnauthenticated(): void {
    clear(root);
    if (!signinExpanded) {
      const btn = el("button", {
        class: "auth-widget__signin-btn",
        text: t("auth.signIn"),
        on: {
          click: () => {
            signinExpanded = true;
            renderUnauthenticated();
          },
        },
      });
      root.appendChild(btn);
    } else {
      const googleBtn = el("button", {
        class: "auth-widget__provider-btn auth-widget__provider-btn--google",
        text: t("auth.google"),
        on: {
          click: () => {
            void openSignIn("google").finally(() => { void render(); });
          },
        },
      });
      const githubBtn = el("button", {
        class: "auth-widget__provider-btn auth-widget__provider-btn--github",
        text: t("auth.github"),
        on: {
          click: () => {
            void openSignIn("github").finally(() => { void render(); });
          },
        },
      });
      root.appendChild(googleBtn);
      root.appendChild(githubBtn);
    }
  }

  function renderIncomplete(): void {
    clear(root);
    const link = el("a", {
      class: "auth-widget__complete-link",
      text: t("auth.completeProfile"),
      attrs: { href: landingUrl + "/profile" },
    });
    const signoutBtn = createSignoutButton("auth-widget__signout-btn");
    const container = el(
      "div",
      { class: "auth-widget__profile auth-widget__profile--incomplete" },
      [link, signoutBtn],
    );
    root.appendChild(container);
  }

  function renderAuthenticated(profile: Profile & { handle: string }): void {
    clear(root);

    const src = avatarSrc(profile.avatarUrl);
    const avatar = el("img", {
      class: "auth-widget__avatar",
      attrs: {
        ...(src !== undefined ? { src } : {}),
        alt: profile.displayName,
        width: "32",
        height: "32",
      },
    });

    const nameSpan = el("span", {
      class: "auth-widget__name",
      text: truncateName(profile.displayName),
    });

    const profileDiv = el("div", { class: "auth-widget__profile" }, [avatar, nameSpan]);
    profileDiv.addEventListener("click", () => {
      dropdownOpen = !dropdownOpen;
      if (dropdownOpen) {
        root.appendChild(buildDropdown());
      } else {
        const existing = root.querySelector(".auth-widget__dropdown");
        if (existing) root.removeChild(existing);
      }
    });

    root.appendChild(profileDiv);

    if (dropdownOpen) {
      root.appendChild(buildDropdown());
    }
  }

  function buildDropdown(): HTMLElement {
    const profileLink = el("a", {
      class: "auth-widget__dropdown-item",
      text: t("auth.profile"),
      attrs: { href: landingUrl + "/profile" },
    });

    const signoutBtn = createSignoutButton(
      "auth-widget__dropdown-item auth-widget__dropdown-item--signout",
    );

    return el("div", { class: "auth-widget__dropdown" }, [profileLink, signoutBtn]);
  }

  async function render(): Promise<void> {
    signinExpanded = false;
    dropdownOpen = false;
    renderLoading();

    const profile = await fetchMe();
    const state = classifyAuthState(profile);

    switch (state.kind) {
      case "unauthenticated":
        renderUnauthenticated();
        return;
      case "incomplete":
        renderIncomplete();
        return;
      case "authenticated":
        renderAuthenticated(state.profile);
        return;
    }
  }

  function onOutsideClick(e: Event): void {
    if (!(e.target instanceof Node) || !root.contains(e.target)) {
      const dropdown = root.querySelector(".auth-widget__dropdown");
      if (dropdown) {
        root.removeChild(dropdown);
        dropdownOpen = false;
      }
    }
  }

  const onAuthChange = (): void => { void render(); };

  window.addEventListener(BN_AUTH_CHANGE, onAuthChange);
  document.addEventListener("click", onOutsideClick);

  void render();

  return {
    root,
    destroy(): void {
      window.removeEventListener(BN_AUTH_CHANGE, onAuthChange);
      document.removeEventListener("click", onOutsideClick);
    },
  };
}
